// Spanish "importe con letra" for the cotización PDF. Turns 136800 into
// "Ciento treinta y seis mil ochocientos pesos 00/100 MXN" — the exact shape of
// the reference document. Moneda is fixed to Mexican pesos (there is no FX
// infrastructure), so the currency label is hardcoded here too.

const UNIDADES = [
    '',
    'uno',
    'dos',
    'tres',
    'cuatro',
    'cinco',
    'seis',
    'siete',
    'ocho',
    'nueve',
    'diez',
    'once',
    'doce',
    'trece',
    'catorce',
    'quince',
    'dieciséis',
    'diecisiete',
    'dieciocho',
    'diecinueve',
    'veinte',
    'veintiuno',
    'veintidós',
    'veintitrés',
    'veinticuatro',
    'veinticinco',
    'veintiséis',
    'veintisiete',
    'veintiocho',
    'veintinueve',
];

const DECENAS = [
    '',
    '',
    '',
    'treinta',
    'cuarenta',
    'cincuenta',
    'sesenta',
    'setenta',
    'ochenta',
    'noventa',
];

const CENTENAS = [
    '',
    'ciento',
    'doscientos',
    'trescientos',
    'cuatrocientos',
    'quinientos',
    'seiscientos',
    'setecientos',
    'ochocientos',
    'novecientos',
];

// "uno" apocopates to "un" before a masculine noun or the multipliers mil/millón:
// veintiún mil, treinta y un pesos, un millón.
function apocopate(words: string): string {
    if (words.endsWith('veintiuno')) {
        return words.slice(0, -'veintiuno'.length) + 'veintiún';
    }
    if (words.endsWith('uno')) {
        return words.slice(0, -'uno'.length) + 'un';
    }
    return words;
}

// 0–99
function tens(n: number): string {
    if (n < 30) return UNIDADES[n];
    const d = Math.floor(n / 10);
    const u = n % 10;
    return DECENAS[d] + (u ? ' y ' + UNIDADES[u] : '');
}

// 0–999
function hundreds(n: number): string {
    if (n === 100) return 'cien';
    const c = Math.floor(n / 100);
    const r = n % 100;
    const head = c ? CENTENAS[c] : '';
    const tail = r ? tens(r) : '';
    return [head, tail].filter(Boolean).join(' ');
}

// 0–999,999,999
function integerToWords(n: number): string {
    if (n === 0) return 'cero';

    const millones = Math.floor(n / 1_000_000);
    const miles = Math.floor((n % 1_000_000) / 1000);
    const resto = n % 1000;

    const parts: string[] = [];

    if (millones > 0) {
        parts.push(
            millones === 1
                ? 'un millón'
                : apocopate(hundreds(millones)) + ' millones',
        );
    }

    if (miles > 0) {
        parts.push(miles === 1 ? 'mil' : apocopate(hundreds(miles)) + ' mil');
    }

    if (resto > 0) {
        parts.push(hundreds(resto));
    }

    return parts.join(' ');
}

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * "Importe con letra" in Mexican pesos.
 * amountInWords(136800) -> "Ciento treinta y seis mil ochocientos pesos 00/100 MXN"
 * The integer part apocopates before "pesos" (21 -> "veintiún pesos"); the
 * centavos print as a two-digit NN/100 fraction, the invoice convention.
 */
export function amountInWords(amount: number): string {
    const safe = isNaN(amount) ? 0 : Math.abs(amount);
    const integerPart = Math.floor(safe);
    const cents = Math.round((safe - integerPart) * 100);

    // Rounding centavos can tip 99.999 -> 100 cents; carry it into the pesos.
    const carriedInteger = integerPart + (cents === 100 ? 1 : 0);
    const carriedCents = cents === 100 ? 0 : cents;

    const words = apocopate(integerToWords(carriedInteger));
    const noun = carriedInteger === 1 ? 'peso' : 'pesos';
    const centsText = carriedCents.toString().padStart(2, '0');

    return capitalize(`${words} ${noun} ${centsText}/100 MXN`);
}
